<template>
    <model-form
        :title="
            readoutGroup && readoutGroup.id ? `Edit ${readoutGroup.name}` : 'Create Readout Group'
        "
        :formFields="formFields"
        @save="save"
        @cancel="cancel"
    />
</template>
<script setup lang="ts">
import { ref, onMounted } from "vue";
import { ReadoutGroup, SavedReadoutGroup } from "@/services/readoutGroups/readoutGroupsAPI";
import { AbstractSchemaConfig } from "@/services/schemas/manageSchemasAPI";
import {
    getAttributeNamesFromSchemaAttributeMap, getAttributeOptions, getAttributesAsList,
    getEntryRelationAttributeOptions,
    getThingSchemaForId, Option,
} from "@/lib/thingUtils";
import { buildMultiSelectField, buildNameField, buildSelectField } from "@/lib/vueModelUtils";
import { buildSchemaSelectFormField } from "@/lib/schemaUtils";
import _ from "lodash";
import ModelForm from "@/components/common/models/ModelForm.vue";

const { readoutGroup, abstractSchemaConfig } = defineProps<{
    readoutGroup: SavedReadoutGroup | null;
    abstractSchemaConfig: AbstractSchemaConfig;
}>();

const emit = defineEmits<{
    (e: "update", item: SavedReadoutGroup): void;
    (e: "create", item: ReadoutGroup): void;
    (e: "closed"): void;
}>();

const selectedThingSchema = ref();
const nameField = ref();
const thingSchemaField = ref();
const descriptionField = ref();
const requestedOneTimeAttributesField = ref();
const filterEntryRelationAttributeField = ref();
const requestedTimeSeriesAttributesField = ref();
const filterOnSequenceNumberField = ref();
const recordTimeWithMeasurementsField = ref();

const formFields = ref<any>([]);

onMounted(() => {
    buildFormFields();
});

function getCurrentAttributeValuesForSelectedSchema(schemaAttributes: any) {
    const attributeNames = getAttributeNamesFromSchemaAttributeMap(schemaAttributes);
    if (_.isNil(readoutGroup)) {
        return [];
    }
    return readoutGroup.requestedAttributes
        .filter((requestedAttribute) => {
            return attributeNames.includes(requestedAttribute.attributeName);
        })
        .map((requestedAttribute) => {
            return requestedAttribute.attributeName;
        });
}

function buildFormFields(): void {
    selectedThingSchema.value = getThingSchemaForId(
        abstractSchemaConfig,
        readoutGroup?.thingSchemaId || abstractSchemaConfig.thingSchemas[0].id
    );

    nameField.value = buildNameField(readoutGroup?.name);

    descriptionField.value = {
        fieldType: "text",
        fieldDisplayName: "Description",
        cols: 12,
        value: readoutGroup?.description,
    };

    requestedOneTimeAttributesField.value = buildMultiSelectField({
        name: "Requested One-Time Attributes",
    });

    requestedTimeSeriesAttributesField.value = buildMultiSelectField({
        name: "Requested Time-Series Attributes",
    });

    thingSchemaField.value = buildSchemaSelectFormField(
        abstractSchemaConfig,
        selectedThingSchema.value!,
        (newSelectedThingSchema) => {
            selectedThingSchema.value = newSelectedThingSchema;
            updateSelectedSchema();
        }
    );

    const entryRelationAttributeOptions = selectedThingSchema.value
        ? getEntryRelationAttributeOptions(selectedThingSchema.value.oneTimeAttributes)
        : [];

    filterEntryRelationAttributeField.value = buildSelectField({
        name: "Filter on Entry Relation Attribute",
        options: entryRelationAttributeOptions,
        value: readoutGroup?.filterEntryRelationAttribute,
    });

    filterOnSequenceNumberField.value = {
        fieldType: "boolean",
        fieldDisplayName: "Filter on Sequence Number",
        value: readoutGroup?.filterOnSequenceNumber,
    };

    recordTimeWithMeasurementsField.value = {
        fieldType: "boolean",
        fieldDisplayName: "Record Time with Measurements",
        value: readoutGroup?.recordTimeWithMeasurements,
    };

    updateSelectedSchema();

    formFields.value = [
        nameField.value,
        thingSchemaField.value,
        descriptionField.value,
        requestedOneTimeAttributesField.value,
        requestedTimeSeriesAttributesField.value,
        filterEntryRelationAttributeField.value,
        recordTimeWithMeasurementsField.value,
        // TODO: taking this out for now, until we clarify how we want it to work
        //  filterOnSequenceNumberField.value
    ];
}

function updateSelectedSchema(): void {

    const oneTimeAttributes = selectedThingSchema.value?.oneTimeAttributes ?? {};
    const timeSeriesAttributes = selectedThingSchema.value?.timeSeriesAttributes ?? {};

    updateRequestedAttributeSelectFieldOptionsAndValues(
        requestedOneTimeAttributesField.value,
        oneTimeAttributes
    );

    updateRequestedAttributeSelectFieldOptionsAndValues(
        requestedTimeSeriesAttributesField.value,
        timeSeriesAttributes
    );

    filterEntryRelationAttributeField.value.options = getEntryRelationAttributeOptions(
        oneTimeAttributes
    );
}

function updateRequestedAttributeSelectFieldOptionsAndValues(field, attributesMap): void {
    field.options = getValidAttributeOptions(attributesMap);
    field.values = getCurrentAttributeValuesForSelectedSchema(attributesMap);
}

function getValidAttributeOptions(attributesMap): Option[] {
    const attributes = getAttributesAsList(attributesMap);
    const validAttributes = attributes.filter((attribute) => {
        // only show attributes that are not subordinate to another attribute - units attributes
        // for example are tied to their value attributes when selecting them for ReadoutGroups
        return _.isNil(attribute.subordinate_to_attribute);
    });
    return getAttributeOptions(validAttributes);
}

function save(): void {
    const requestedOneTimeAttributesFormField = requestedOneTimeAttributesField.value;
    const requestedOneTimeAttributes = requestedOneTimeAttributesFormField.values.map(
        (attributeName) => {
            return {
                attributeName: attributeName,
            };
        }
    );

    const requestedTimeSeriesAttributesFormField = requestedTimeSeriesAttributesField.value;
    const requestedTimeSeriesAttributes =
        requestedTimeSeriesAttributesFormField.values.map((attributeName) => {
            return {
                attributeName: attributeName,
            };
        });

    const nameFormField = nameField.value;
    const descriptionFormField = descriptionField.value;
    const filterEntryRelationAttributeFormField = filterEntryRelationAttributeField.value;
    const filterOnSequenceNumberFormField = filterOnSequenceNumberField.value;
    const recordWithTimeMeasurementsFormField = recordTimeWithMeasurementsField.value;

    const readoutGroupToSave = {
        ...readoutGroup,
        name: nameFormField.value,
        description: descriptionFormField.value,
        thingSchemaId: selectedThingSchema.value.id,
        requestedAttributes: [
            ...requestedOneTimeAttributes,
            ...requestedTimeSeriesAttributes,
        ],
        filterEntryRelationAttribute: filterEntryRelationAttributeFormField.value,
        filterOnSequenceNumber: filterOnSequenceNumberFormField.value,
        recordTimeWithMeasurements: recordWithTimeMeasurementsFormField.value || false,
    };

    if (readoutGroup?.id) {
        emit("update", readoutGroupToSave as SavedReadoutGroup);
    } else {
        emit("create", readoutGroupToSave as ReadoutGroup);
    }
}

function cancel() {
    emit("closed");
}
</script>
